
export interface ExtractedTableData {
  headers: string[];
  rows: any[][];
}

export function extractSKUs(tableData: ExtractedTableData): string[] {
  const skuSet = new Set<string>();
  const skuColumnIndex = tableData.headers.findIndex(
    (h) =>
      h.toLowerCase().includes("sku") ||
      h.toLowerCase() === "seller sku" ||
      h.toLowerCase() === "product sku"
  );

  if (skuColumnIndex >= 0) {
    tableData.rows.forEach((row) => {
      const skuValue = row[skuColumnIndex];
      if (skuValue) {
        const valueStr = String(skuValue).trim();
        if (isSKUPattern(valueStr)) {
          skuSet.add(valueStr);
        }
      }
    });
  } else {
    // Try to find SKUs in any column (optional)
    tableData.rows.forEach((row) => {
      row.forEach((cell) => {
        if (cell) {
          const valueStr = String(cell).trim();
          if (isSKUPattern(valueStr)) {
            skuSet.add(valueStr);
          }
        }
      });
    });
  }

  return Array.from(skuSet);
}

function isSKUPattern(value: string): boolean {
  if (!value || typeof value !== "string") return false;

  const trimmedValue = value.trim();

  const skuPatterns = [
    /^[A-Z]+-\d{3}$/,
    /^[A-Z]+-\d{2,4}$/,
    /^[A-Z]+\d{3}$/,
    /^[A-Z]+_\d{3}$/,
    /^[A-Z]+\d{2,4}$/,
    /^[A-Z]+-\d+[A-Z]*$/,
    /^[A-Z]+\d+[A-Z]*$/,
  ];

  return skuPatterns.some((pattern) => pattern.test(trimmedValue));
}

export async function extractSelectedData(): Promise<ExtractedTableData | null> {
  try {
    let tableData: ExtractedTableData | null = null;

    await Excel.run(async (context) => {
      try {
        const range = context.workbook.getSelectedRange();
        range.load(["values", "rowCount", "columnCount", "address", "rowIndex", "columnIndex"]);
        await context.sync();

        console.log("Selected range:", range.address);

        if (range.values && range.rowCount > 0) {
          const worksheet = range.worksheet;
          worksheet.load("name");
          await context.sync();

          // **STRATEGY 1: Check if selection is part of an Excel Table**
          const actualHeaders = await findHeadersInExcelTable(context, worksheet, range);

          if (actualHeaders) {
            console.log("✅ Found headers from Excel Table:", actualHeaders.headers);
            tableData = actualHeaders;
          } else {
            // **STRATEGY 2: Search upward to find header row**
            const headersFromUpwardSearch = await findHeadersUpward(context, worksheet, range);

            if (headersFromUpwardSearch) {
              console.log("✅ Found headers from upward search:", headersFromUpwardSearch.headers);
              tableData = headersFromUpwardSearch;
            } else {
              // **STRATEGY 3: Use first row if it looks like headers**
              const headersFromSelection = findHeadersInSelection(range);

              if (headersFromSelection) {
                console.log("✅ Found headers in selection:", headersFromSelection.headers);
                tableData = headersFromSelection;
              } else {
                // **STRATEGY 4: Generic fallback**
                console.warn("⚠️ No headers found, using generic column names");
                tableData = createGenericTableData(range);
              }
            }
          }

          if (tableData && tableData.headers.length > 0 && tableData.rows.length > 0) {
            console.log("✅ Final table data:", {
              headers: tableData.headers.length,
              rows: tableData.rows.length,
              columns: tableData.headers,
              sampleRow: tableData.rows[0]?.slice(0, 5),
            });
          }
        }
      } catch (error) {
        console.log("Error getting selection:", error);
      }
    });

    return tableData;
  } catch (error) {
    console.error("Error in extractSelectedData:", error);
    return null;
  }
}

async function findHeadersInExcelTable(
  context: Excel.RequestContext,
  worksheet: Excel.Worksheet,
  selectedRange: Excel.Range
): Promise<ExtractedTableData | null> {
  try {
    const tables = worksheet.tables;
    tables.load("items");
    await context.sync();

    for (const table of tables.items) {
      table.load("name");
      await context.sync();

      const tableRange = table.getRange();
      tableRange.load(["address", "rowIndex", "columnIndex", "rowCount", "columnCount"]);
      await context.sync();

      // Check if selection overlaps with this table
      const selectionStart = selectedRange.rowIndex;
      const selectionEnd = selectedRange.rowIndex + selectedRange.rowCount - 1;
      const tableStart = tableRange.rowIndex;
      const tableEnd = tableRange.rowIndex + tableRange.rowCount - 1;

      const overlaps = selectionStart <= tableEnd && selectionEnd >= tableStart;

      if (overlaps) {
        console.log(`✅ Selection overlaps with table: ${table.name}`);

        // Get the header row from the table
        const headerRange = table.getHeaderRowRange();
        headerRange.load("values");
        await context.sync();

        const tableHeaders = headerRange.values[0].map((h: any) => String(h || "").trim());

        const columnOffset = selectedRange.columnIndex - tableRange.columnIndex;
        const selectionColumnCount = selectedRange.columnCount;

        const startIndex = Math.max(0, columnOffset);
        const endIndex = Math.min(tableHeaders.length, startIndex + selectionColumnCount);

        const headers = tableHeaders.slice(startIndex, endIndex);

        const dataRows: any[][] = [];
        for (let i = 0; i < selectedRange.rowCount; i++) {
          const row = selectedRange.values[i];
          if (selectedRange.rowIndex + i === tableRange.rowIndex) continue;

          if (row.some((cell: any) => cell !== null && cell !== undefined && cell !== "")) {
            const slicedRow = row
              .slice(0, headers.length)
              .map((cell: any) => (cell !== null && cell !== undefined ? cell : null));
            dataRows.push(slicedRow);
          }
        }

        return { headers, rows: dataRows };
      }
    }

    return null;
  } catch (error) {
    console.log("No Excel Table found or error:", error);
    return null;
  }
}

async function findHeadersUpward(
  context: Excel.RequestContext,
  worksheet: Excel.Worksheet,
  selectedRange: Excel.Range
): Promise<ExtractedTableData | null> {
  try {
    const usedRange = worksheet.getUsedRange();
    usedRange.load(["rowIndex", "columnIndex", "rowCount", "columnCount"]);
    await context.sync();

    const selectionStartRow = selectedRange.rowIndex;
    const selectionStartCol = selectedRange.columnIndex;
    const selectionColCount = selectedRange.columnCount;
    const searchStartRow = usedRange.rowIndex;

    console.log(
      `🔍 Searching for headers from row ${searchStartRow} to row ${selectionStartRow - 1}`
    );

    for (let rowIdx = searchStartRow; rowIdx < selectionStartRow; rowIdx++) {
      try {
        const potentialHeaderRange = worksheet.getRangeByIndexes(
          rowIdx,
          selectionStartCol,
          1,
          selectionColCount
        );
        potentialHeaderRange.load("values");
        await context.sync();

        const potentialHeaderRow = potentialHeaderRange.values[0];

        if (looksLikeHeaders(potentialHeaderRow)) {
          const headers = potentialHeaderRow.map((h: any, idx: number) =>
            String(h || `Column${idx + 1}`).trim()
          );

          try {
            const nextRowRange = worksheet.getRangeByIndexes(
              rowIdx + 1,
              selectionStartCol,
              1,
              selectionColCount
            );
            nextRowRange.load("values");
            await context.sync();

            const nextRow = nextRowRange.values[0];
            const hasDataBelow = nextRow.some(
              (cell: any) => cell !== null && cell !== undefined && cell !== ""
            );

            if (hasDataBelow) {
              console.log(`✅ Found valid headers at row ${rowIdx}:`, headers);

              // Extract data rows from selection
              const dataRows: any[][] = [];
              for (let i = 0; i < selectedRange.rowCount; i++) {
                const row = selectedRange.values[i];
                if (row.some((cell: any) => cell !== null && cell !== undefined && cell !== "")) {
                  dataRows.push(
                    row.map((cell: any) => (cell !== null && cell !== undefined ? cell : null))
                  );
                }
              }

              return { headers, rows: dataRows };
            }
          } catch (e) {
            continue;
          }
        }
      } catch (e) {
        continue;
      }
    }

    return null;
  } catch (error) {
    console.log("Error in upward search:", error);
    return null;
  }
}

function findHeadersInSelection(selectedRange: Excel.Range): ExtractedTableData | null {
  const firstRow = selectedRange.values[0];

  if (looksLikeHeaders(firstRow)) {
    const headers = firstRow.map((h: any, idx: number) => String(h || `Column${idx + 1}`).trim());
    const dataRows: any[][] = [];

    for (let i = 1; i < selectedRange.rowCount; i++) {
      const row = selectedRange.values[i];
      if (row.some((cell: any) => cell !== null && cell !== undefined && cell !== "")) {
        dataRows.push(row.map((cell: any) => (cell !== null && cell !== undefined ? cell : null)));
      }
    }

    if (dataRows.length > 0) {
      return { headers, rows: dataRows };
    }
  }

  return null;
}

function createGenericTableData(selectedRange: Excel.Range): ExtractedTableData {
  const headers = Array.from({ length: selectedRange.columnCount }, (_, i) => `Column${i + 1}`);
  const dataRows: any[][] = [];

  for (let i = 0; i < selectedRange.rowCount; i++) {
    const row = selectedRange.values[i];
    if (row.some((cell: any) => cell !== null && cell !== undefined && cell !== "")) {
      dataRows.push(row.map((cell: any) => (cell !== null && cell !== undefined ? cell : null)));
    }
  }

  return { headers, rows: dataRows };
}

function looksLikeHeaders(row: any[]): boolean {
  if (!row || row.length === 0) return false;

  let textCount = 0;
  let nonEmptyCount = 0;

  for (const cell of row) {
    if (cell !== null && cell !== undefined && cell !== "") {
      nonEmptyCount++;
      const str = String(cell).trim();

      const isNotPureNumber = isNaN(Number(str)) || str.length > 10;

      if (isNotPureNumber) {
        textCount++;
      }
    }
  }

  return nonEmptyCount > 0 && textCount / nonEmptyCount >= 0.5;
}
